/**
 * Tenant dashboard V2 tab model (COL-533).
 *
 * The V1 dashboard has nine tabs, all always present. V2 has five that unlock
 * by journey phase, plus a separate three for applicants who have no booking
 * yet. This module owns both the grouping and the mapping back from the old
 * tab names, so no component has to know which old tab became which new one.
 *
 * Why the legacy mapping matters: five edge functions
 * (`send-contract-to-docusign`, `get-docusign-signing-url`,
 * `send-payment-reminder`, `send-protocol-email`, `protocol-reminder-tick`)
 * email `/dashboard?tab=contract`, `?tab=checkin` and `?tab=payments` links.
 * Those are already in tenants' inboxes and keep arriving from scheduled
 * ticks, so the names cannot simply stop resolving — all three now land on
 * "Mein Zimmer", which is where that content moved.
 */

import {
  isPreMoveInJourneyStage,
  isResidentJourneyStage,
  type TenantJourneyStageId,
} from "@/lib/tenantJourney";

/**
 * The escape hatch: `?dashboard=v1` forces the old page regardless of the flag.
 *
 * Lives here rather than in `DashboardRoute` because the V2 shell builds
 * hand-off links with it too, and importing them from the route would make the
 * route and the page import each other.
 */
export const FORCE_V1_PARAM = "dashboard";
export const FORCE_V1_VALUE = "v1";

/** Tabs a tenant with a booking sees. */
export const TENANT_V2_TABS = [
  "overview",
  "room",
  "community",
  "referrals",
  "help",
] as const;

/** Tabs an applicant with no booking sees instead. */
export const APPLICANT_V2_TABS = ["applications", "saved", "help"] as const;

export type TenantV2Tab = (typeof TENANT_V2_TABS)[number];
export type ApplicantV2Tab = (typeof APPLICANT_V2_TABS)[number];
export type DashboardV2Tab = TenantV2Tab | ApplicantV2Tab;

/**
 * Where each V1 tab ends up. `perks` is deliberately absent from the V2 tab
 * list — the design deletes it (it renders an empty placeholder today) — so it
 * resolves to the overview rather than to a tab that no longer exists.
 */
const LEGACY_TAB_MAP: Record<string, DashboardV2Tab> = {
  home: "overview",
  // Vertrag + Zahlungen + Ein-/Auszug + Dokumente all merge into Mein Zimmer.
  contract: "room",
  payments: "room",
  checkin: "room",
  documents: "room",
  maintenance: "help",
  community: "community",
  referrals: "referrals",
  perks: "overview",
  favorites: "saved",
};

/**
 * Resolve a `?tab=` value from a link that predates V2.
 *
 * Returns `null` for an unknown value so the caller can fall back to its own
 * default rather than being handed a wrong tab. Already-V2 names pass through
 * unchanged, so a link built after the cutover still works.
 */
export function resolveLegacyTabParam(
  param: string | null | undefined
): DashboardV2Tab | null {
  if (!param) return null;
  const key = param.trim().toLowerCase();
  if ((TENANT_V2_TABS as readonly string[]).includes(key)) {
    return key as DashboardV2Tab;
  }
  if ((APPLICANT_V2_TABS as readonly string[]).includes(key)) {
    return key as DashboardV2Tab;
  }
  return LEGACY_TAB_MAP[key] ?? null;
}

/**
 * Which tabs are available at a given journey stage.
 *
 * Community and Referrals stay shut until the tenancy is real — before that
 * there are no flatmates to see and no reward to earn. Everything else is
 * available throughout, because a tenant who needs their contract or a
 * document should never find the tab missing.
 */
/**
 * Booking statuses that mean "this is a real tenancy", for deciding whether
 * someone sees the resident tabs or the applicant frame.
 *
 * Deliberately NOT `ACTIVE_BOOKING_STATUSES`. That set is dunning's — it is
 * mirrored from `supabase/functions/_shared/dunning.ts` and guarded by a
 * parity test, because it decides who gets charged and chased for rent.
 * Widening it to fix a TAB would change rent collection, which is not what
 * anyone asked for.
 *
 * This set is the whole tenancy lifecycle instead. A tenant who has given
 * notice, is moving out, or has moved out is MORE certainly a real tenant
 * than one who is merely `approved` — and they are exactly the people who
 * need the move-out protocol, which the applicant frame does not show. The
 * first version of this fix only covered the rent-collecting statuses, so a
 * tenant on notice with a paper contract was sent back to the applicant
 * screens and lost their move-out protocol.
 */
export const TENANCY_STARTED_STATUSES: readonly string[] = [
  "confirmed",
  "active",
  "approved",
  "notice_given",
  "move_out_pending",
  "moved_out",
  "terminated",
];

/** Does this booking's status mean the tenancy is real? */
export function isTenancyStarted(status: unknown): boolean {
  return TENANCY_STARTED_STATUSES.includes(String(status ?? "").toLowerCase());
}

export function visibleTabsForStage(
  stage: TenantJourneyStageId | null | undefined,
  hasBooking: boolean,
  /**
   * Booking status is in `ACTIVE_BOOKING_STATUSES` — i.e. this is a live
   * tenancy regardless of what the contract ladder can prove. Defaults to
   * false so an un-updated caller keeps the stricter behaviour.
   */
  tenancyStarted = false
): readonly DashboardV2Tab[] {
  if (!hasBooking) return APPLICANT_V2_TABS;
  /*
    An application is a tenancy that has not started yet.

    `hasBooking` comes from `useTenantBooking`, whose status list includes
    `pending` — which is exactly what the application form writes. So an
    applicant waiting to hear back was handed the TENANT tabs: an Übersicht
    with no action to take and a Mein Zimmer with no contract, no deposit and
    no handover on it, while the applicant frame the design draws for them was
    unreachable by construction. Before a contract is released there is
    nothing on the tenant tabs, so the stage decides, not the row's existence.
  */
  /*
    ...unless the tenancy demonstrably started.

    Every contract gate on the stage ladder is anded with
    `hasContractDocument`, deliberately: a `contract_status` claim with no
    artefact must not promote anyone. But a contract signed ON PAPER leaves no
    artefact either, and that tenant is living in the room. They parked at
    `applied` and were handed the APPLICANT tabs -- losing Mein Zimmer and
    their payments while rent was being charged against the same booking.

    `ACTIVE_BOOKING_STATUSES` is the set dunning collects against, so it is
    the sharpest available answer to "is this a real tenancy": if we would
    chase them for rent, they are not an applicant. It is evidence about the
    tenancy, not a claim about the contract, so it does not reopen the hole
    the document gate closed -- the ladder still refuses to promote them past
    a gate nothing proved; they simply keep the resident tabs.
  */
  if (stage === "applied" && !tenancyStarted) return APPLICANT_V2_TABS;
  if (!stage || stage === "applied") return ["overview", "room", "help"];

  const settled =
    isResidentJourneyStage(stage) || !isPreMoveInJourneyStage(stage);
  return settled ? TENANT_V2_TABS : (["overview", "room", "help"] as const);
}

/**
 * Clamp a requested tab to one that is actually available right now.
 *
 * A deep link can outlive the state that justified it — a move-in protocol
 * email sent last month still points at Mein Zimmer after move-out — so the
 * requested tab is honoured only when the stage still offers it.
 */
export function resolveActiveTab(
  requested: DashboardV2Tab | null,
  stage: TenantJourneyStageId | null | undefined,
  hasBooking: boolean,
  tenancyStarted = false
): DashboardV2Tab {
  const visible = visibleTabsForStage(stage, hasBooking, tenancyStarted);
  if (requested && visible.includes(requested)) return requested;
  return visible[0];
}
